import { useEffect, useState, useCallback } from "react";
import { api, apiError, API } from "@/lib/api";
import { toast } from "sonner";
import { Users, ShoppingCart, Globe2, MapPin, Download, Mail, Search, RefreshCw } from "lucide-react";

const RANGES = [
  { key: "1d", label: "Today" },
  { key: "7d", label: "Last 7d" },
  { key: "30d", label: "Last 30d" },
  { key: "90d", label: "Last 90d" },
  { key: "1y", label: "Last 1y" },
  { key: "all", label: "All-time" },
];

const _fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" });
  } catch { return iso.slice(0, 10); }
};

export default function AnalyticsPanel() {
  const [range, setRange] = useState("30d");
  const [q, setQ] = useState("");
  const [country, setCountry] = useState("");
  const [onlyLoggedIn, setOnlyLoggedIn] = useState(false);
  const [summary, setSummary] = useState(null);
  const [visitors, setVisitors] = useState({ items: [], total: 0 });
  const [carts, setCarts] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const [sendingFor, setSendingFor] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, v, c] = await Promise.all([
        api.get(`/admin/analytics/summary?range=${range}`),
        api.get(`/admin/analytics/visitors?range=${range}&q=${encodeURIComponent(q)}&country=${country}&only_logged_in=${onlyLoggedIn}&limit=100`),
        api.get(`/admin/analytics/abandoned-carts?range=${range}&q=${encodeURIComponent(q)}&limit=100`),
      ]);
      setSummary(s.data);
      setVisitors(v.data);
      setCarts(c.data);
    } catch (e) { toast.error(apiError(e)); }
    setLoading(false);
  }, [range, q, country, onlyLoggedIn]);

  useEffect(() => { load(); }, [load]);

  const sendReminder = async (row) => {
    if (!window.confirm(`Send an abandoned cart reminder to ${row.email}?`)) return;
    setSendingFor(row.user_id);
    try {
      const { data } = await api.post(`/admin/abandoned-carts/${row.user_id}/remind`);
      if (data.ok) toast.success(`Reminder sent to ${data.email}`);
      else toast.error(data.error || "Send failed — check Resend logs");
      load();
    } catch (e) { toast.error(apiError(e)); }
    setSendingFor("");
  };

  const downloadXlsx = (kind) => {
    const url = kind === "visitors"
      ? `${API}/admin/analytics/visitors/export.xlsx?range=${range}&q=${encodeURIComponent(q)}&country=${country}&only_logged_in=${onlyLoggedIn}`
      : `${API}/admin/analytics/abandoned-carts/export.xlsx?range=${range}&q=${encodeURIComponent(q)}`;
    // Same-origin so cookies attach; use anchor download
    const a = document.createElement("a");
    a.href = url;
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  return (
    <div className="space-y-6" data-testid="analytics-panel">
      {/* Header + range picker + search */}
      <div className="border-2 border-[#09090b] bg-white p-5">
        <div className="flex flex-wrap items-center gap-3 justify-between mb-4">
          <div>
            <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#ea580c] mb-1">Advanced Analytics</div>
            <div className="font-display text-2xl font-black text-[#09090b]">Visitors, Carts &amp; Follow-ups</div>
          </div>
          <button
            onClick={load}
            className="inline-flex items-center gap-2 px-3 py-2 border-2 border-[#09090b] bg-white text-sm font-bold hover:bg-[#f4f4f5]"
            data-testid="btn-analytics-refresh"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          {RANGES.map((r) => (
            <button
              key={r.key}
              onClick={() => setRange(r.key)}
              className={`px-3 py-1.5 text-xs font-bold border-2 ${range === r.key ? "bg-[#09090b] text-white border-[#09090b]" : "bg-white text-[#09090b] border-[#e7e5e4] hover:border-[#09090b]"}`}
              data-testid={`range-${r.key}`}
            >
              {r.label}
            </button>
          ))}
          <div className="ml-auto flex gap-2 items-center">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-[#71717a]" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search name / email / city…"
                className="border-2 border-[#e7e5e4] pl-8 pr-3 py-2 text-sm focus:border-[#09090b] outline-none w-64"
                data-testid="analytics-search"
              />
            </div>
            <input
              value={country}
              onChange={(e) => setCountry(e.target.value.toUpperCase())}
              placeholder="IN / US"
              maxLength={2}
              className="border-2 border-[#e7e5e4] px-2 py-2 text-sm w-16 text-center focus:border-[#09090b] outline-none"
              data-testid="analytics-country"
            />
            <label className="flex items-center gap-2 text-xs font-bold cursor-pointer select-none">
              <input type="checkbox" checked={onlyLoggedIn} onChange={(e) => setOnlyLoggedIn(e.target.checked)} data-testid="analytics-only-logged-in" />
              Logged-in only
            </label>
          </div>
        </div>
      </div>

      {/* KPI cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
          {[
            { label: "Human Visitors", value: summary.human_visitors ?? summary.visitors, icon: Users, color: "border-l-[#ea580c]", hint: "Unique humans in range" },
            { label: "Total Hits", value: summary.total_hits ?? 0, icon: Globe2, color: "border-l-[#0284c7]", hint: "All clicks incl. bots" },
            { label: "Bot Traffic", value: summary.bot_hits ?? 0, icon: Globe2, color: "border-l-[#94a3b8]", hint: "Googlebot, Bing, crawlers" },
            { label: "Logged-in", value: summary.logged_in_visitors, icon: Users, color: "border-l-[#0891b2]" },
            { label: "Countries", value: summary.unique_countries, icon: Globe2, color: "border-l-[#16a34a]" },
            { label: "Cities", value: summary.unique_cities, icon: MapPin, color: "border-l-[#7c3aed]" },
            { label: "Orders", value: summary.orders, icon: ShoppingCart, color: "border-l-[#0f766e]" },
            { label: "Abandoned Carts", value: summary.abandoned_carts, icon: ShoppingCart, color: "border-l-[#dc2626]" },
          ].map((s) => {
            const Icon = s.icon;
            return (
              <div key={s.label} className={`bg-white border border-[#e7e5e4] border-l-4 ${s.color} p-4`} data-testid={`analytics-kpi-${s.label.toLowerCase().replace(/\s/g, '-')}`}>
                <div className="text-[10px] font-bold tracking-[0.15em] uppercase text-[#71717a] flex items-center gap-1">
                  <Icon className="w-3 h-3" /> {s.label}
                </div>
                <div className="mt-1 font-display text-2xl font-black text-[#09090b]">{s.value}</div>
              </div>
            );
          })}
        </div>
      )}

      {/* Visitors table */}
      <div className="border-2 border-[#09090b] bg-white">
        <div className="flex items-center justify-between px-4 py-3 border-b-2 border-[#09090b]">
          <div className="font-display text-lg font-black text-[#09090b]">
            Human Visitors <span className="text-[#71717a] font-normal text-sm">({visitors.total})</span>
          </div>
          <button
            onClick={() => downloadXlsx("visitors")}
            className="inline-flex items-center gap-2 px-3 py-1.5 border-2 border-[#09090b] bg-white text-xs font-bold hover:bg-[#f4f4f5]"
            data-testid="btn-download-visitors-xlsx"
          >
            <Download className="w-3 h-3" /> Download Excel
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#fafaf9] border-b border-[#e7e5e4]">
              <tr>
                <th className="text-left px-4 py-2 font-bold text-[#52525b] uppercase text-xs tracking-wider">Name</th>
                <th className="text-left px-4 py-2 font-bold text-[#52525b] uppercase text-xs tracking-wider">Email</th>
                <th className="text-left px-4 py-2 font-bold text-[#52525b] uppercase text-xs tracking-wider">City</th>
                <th className="text-left px-4 py-2 font-bold text-[#52525b] uppercase text-xs tracking-wider">Country</th>
                <th className="text-left px-4 py-2 font-bold text-[#52525b] uppercase text-xs tracking-wider">Hits</th>
                <th className="text-left px-4 py-2 font-bold text-[#52525b] uppercase text-xs tracking-wider">Last seen</th>
              </tr>
            </thead>
            <tbody>
              {visitors.items.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-6 text-center text-[#71717a]">No visitors in this range yet</td></tr>
              )}
              {visitors.items.map((v) => (
                <tr key={v.id} className="border-b border-[#e7e5e4] hover:bg-[#fafaf9]" data-testid={`visitor-row-${v.id}`}>
                  <td className="px-4 py-2 font-medium">{v.name || <span className="text-[#71717a]">Anonymous</span>}</td>
                  <td className="px-4 py-2 text-[#52525b]">{v.email || "—"}</td>
                  <td className="px-4 py-2">{v.city || "—"}</td>
                  <td className="px-4 py-2 font-mono text-xs">{v.country || <span className="text-[#a1a1aa] italic">Unknown</span>}</td>
                  <td className="px-4 py-2 text-center">{v.hits}</td>
                  <td className="px-4 py-2 text-xs text-[#71717a]">{_fmtDate(v.last_seen)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Abandoned carts */}
      <div className="border-2 border-[#dc2626] bg-white">
        <div className="flex items-center justify-between px-4 py-3 border-b-2 border-[#dc2626] bg-[#fef2f2]">
          <div className="font-display text-lg font-black text-[#09090b]">
            Abandoned Carts <span className="text-[#71717a] font-normal text-sm">({carts.total})</span>
          </div>
          <button
            onClick={() => downloadXlsx("abandoned-carts")}
            className="inline-flex items-center gap-2 px-3 py-1.5 border-2 border-[#dc2626] bg-white text-xs font-bold hover:bg-[#fef2f2]"
            data-testid="btn-download-carts-xlsx"
          >
            <Download className="w-3 h-3" /> Download Excel
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#fafaf9] border-b border-[#e7e5e4]">
              <tr>
                <th className="text-left px-4 py-2 font-bold text-[#52525b] uppercase text-xs tracking-wider">Name</th>
                <th className="text-left px-4 py-2 font-bold text-[#52525b] uppercase text-xs tracking-wider">Email</th>
                <th className="text-left px-4 py-2 font-bold text-[#52525b] uppercase text-xs tracking-wider">City / Country</th>
                <th className="text-left px-4 py-2 font-bold text-[#52525b] uppercase text-xs tracking-wider">Items</th>
                <th className="text-left px-4 py-2 font-bold text-[#52525b] uppercase text-xs tracking-wider">First item</th>
                <th className="text-left px-4 py-2 font-bold text-[#52525b] uppercase text-xs tracking-wider">Last reminder</th>
                <th className="text-left px-4 py-2 font-bold text-[#52525b] uppercase text-xs tracking-wider">Action</th>
              </tr>
            </thead>
            <tbody>
              {carts.items.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-6 text-center text-[#71717a]">🎉 No abandoned carts in this range</td></tr>
              )}
              {carts.items.map((c) => (
                <tr key={c.user_id} className="border-b border-[#e7e5e4] hover:bg-[#fafaf9]" data-testid={`abandoned-row-${c.user_id}`}>
                  <td className="px-4 py-2 font-medium">{c.name || <span className="text-[#71717a]">—</span>}</td>
                  <td className="px-4 py-2 text-[#52525b]">{c.email}</td>
                  <td className="px-4 py-2 text-xs">{[c.city, c.country].filter(Boolean).join(", ") || "—"}</td>
                  <td className="px-4 py-2 text-center">{c.item_count}</td>
                  <td className="px-4 py-2 text-xs italic text-[#52525b] max-w-[240px] truncate" title={c.first_item_title}>{c.first_item_title || "—"}</td>
                  <td className="px-4 py-2 text-xs text-[#71717a]">{_fmtDate(c.reminder_sent_at)}</td>
                  <td className="px-4 py-2">
                    <button
                      onClick={() => sendReminder(c)}
                      disabled={sendingFor === c.user_id}
                      className="inline-flex items-center gap-1 px-3 py-1.5 border-2 border-[#ea580c] bg-[#ea580c] text-white text-xs font-bold hover:bg-[#c2410c] hover:border-[#c2410c] disabled:opacity-50 disabled:cursor-not-allowed"
                      data-testid={`btn-remind-${c.user_id}`}
                    >
                      <Mail className="w-3 h-3" /> {sendingFor === c.user_id ? "Sending…" : "Send Follow-up"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
